import io, { Socket } from 'socket.io-client'
import { BotId, UserProfile } from '@feedyou-ai/feedbot-schema'
import { knowledgeBaseApi } from '@features/knowledgeBase/knowledgeBaseApi'
import { BotTesterRunRecord, collapseUnion, IndexRunResult } from '@feedyou/utils'

import { getApiUrl } from '../../apiService'
import { actions } from './socketServerSlice'
// eslint-disable-next-line no-useless-rename
import { appActions as appActions } from '../app'
import { launchHostingActions } from '../launchHosting'
import { tryToApplyDiff } from '../flow/applyDiffService'
import {
  toastNotifyIndexingDone,
  toastNotifyJoinedConnectionInfo,
  toastNotifySaveChangesError
} from './toasts'
import {
  BotState,
  ConnectedSocketUser,
  RoomState,
  SocketClientEvent,
  SocketServerEvent
} from './types'
import { cliLog } from '../../lib/cliLog'
import { invalidateCommentsCache } from '../../features/serverComments'
import { FeedbotDispatch, GetState } from '../types'
import { editingUsersSelector, isUnsavedSelector } from './selectors'
import { SocketLog } from '../../lib/debugging/socketLog'
import { openBotIdSelector } from '../app/selectors'
import { checkDeployedVersion } from '../app/actions'
import { hostingVersionsApi } from '../../features/hostingVersions'
import { ServerApiTag } from '../serverApi'
import { botsApi } from '../../features/serverBots/api/botsApi'
import { isHostingLaunchingSelector } from '../launchHosting/selectors'
import { botTesterApi } from '../../features/bot-tester/botTesterApi'

export let socket: Socket

export function openSocketServer(
  botId: BotId,
  user: ConnectedSocketUser,
  dispatch: FeedbotDispatch,
  getState: GetState
) {
  closeSocketServer()

  cliLog('connecting to server')

  socket = io(generateApiUrl(user, botId))

  socket.on('connect', () => {
    cliLog('connected to socket-server')
    SocketLog.log('Socket In', 'connect')
    dispatch(actions.connect({ socketId: socket.id! }))
  })

  socket.on('disconnect', (reason: any) => {
    cliLog('disconnected from socket-server', reason)
    SocketLog.log('Disconnect', reason)
    dispatch(actions.disconnect())
  })

  socket.on(SocketServerEvent.ROOM_STATE, (roomState: RoomState) => {
    dispatch(actions.newRoomState(roomState))
    SocketLog.log('Socket In', SocketServerEvent.ROOM_STATE, { roomState })
    const state = getState()
    tryToApplyDiff(state, dispatch)
    if (!roomState.isLaunching && isHostingLaunchingSelector(state)) {
      const defaultMessage = 'There was a problem with creating new hosting.'
      dispatch(launchHostingActions.launchHostingFailure(roomState.hostingError || defaultMessage))
    }
  })

  socket.on(SocketServerEvent.NEW_VERSION, () => {
    SocketLog.log('Socket In', SocketServerEvent.NEW_VERSION)
    dispatch(launchHostingActions.launchHostingSuccess())
  })

  socket.on(SocketServerEvent.BOT_TESTER_SCENARIO_FINISH, (runRecord: BotTesterRunRecord) => {
    SocketLog.log('Socket In', SocketServerEvent.BOT_TESTER_SCENARIO_FINISH, { runRecord })

    dispatch(
      botTesterApi.util.invalidateTags([
        ServerApiTag.BOT_TESTER_RECORDED_RUNS(collapseUnion(runRecord.runConfig))
      ])
    )

    dispatch(
      botTesterApi.util.updateQueryData('getTesterScenarios', { botId }, draft => {
        const scenario = draft.find(s => s.scenarioId === runRecord.scenarioId)
        if (scenario) {
          scenario.lastRun = runRecord
        }
      })
    )
  })

  socket.on(SocketServerEvent.RELOAD_BOT, () => {
    SocketLog.log('Socket In', SocketServerEvent.RELOAD_BOT)
    // I don't think we need this anymore! Rtkq just fetches new version now
    // dispatch(serverApi.util?.invalidateTags([ServerApiTag.ALL_BOTS]))
  })

  socket.on(SocketServerEvent.COMMENTS_CHANGE, () => {
    SocketLog.log('Socket In', SocketServerEvent.COMMENTS_CHANGE)
    dispatch(invalidateCommentsCache())
  })

  socket.on(SocketServerEvent.DEPLOY_BOT_FAILURE, (error?: string) => {
    SocketLog.log('Socket In', SocketServerEvent.DEPLOY_BOT_FAILURE, { error })
    const state = getState()
    dispatch(checkDeployedVersion(openBotIdSelector(state)))
  })

  socket.on(SocketServerEvent.DEPLOY_BOT_SUCCESS, () => {
    SocketLog.log('Socket In', `${SocketServerEvent.DEPLOY_BOT_SUCCESS}`)
    const state = getState()
    dispatch(checkDeployedVersion(openBotIdSelector(state)))
  })

  socket.on(SocketServerEvent.SYNC_ERROR, (roomState: RoomState) => {
    SocketLog.log('Socket In', SocketServerEvent.SYNC_ERROR, { roomState })
    dispatch(actions.newRoomState(roomState))
    const state = getState()
    tryToApplyDiff(state, dispatch)
    isUnsavedSelector(state) && toastNotifySaveChangesError()
  })

  socket.on(SocketServerEvent.UPDATE_HOSTING_DONE, () => {
    SocketLog.log('Socket In', `${SocketServerEvent.UPDATE_HOSTING_DONE}`)
    dispatch(appActions.updateHostingSuccess())
    dispatch(
      hostingVersionsApi.util.invalidateTags([
        ServerApiTag.BOT_DEPLOYED_TREE(botId),
        ServerApiTag.HOSTING_VERSIONS,
        ServerApiTag.HOSTING_UPDATE_HISTORY
      ])
    )
  })

  socket.on(SocketServerEvent.NEW_EDITING_USER, (user: UserProfile) => {
    SocketLog.log('Socket In', SocketServerEvent.NEW_EDITING_USER, { user })
    const state = getState()
    if (Object.keys(editingUsersSelector(state)).includes(state.user.info.id as any)) {
      toastNotifyJoinedConnectionInfo(user.name || '')
    }
  })

  socket.on(SocketServerEvent.UPDATE_BOT_CONFIGURATION, () => {
    SocketLog.log('Socket In', SocketServerEvent.UPDATE_BOT_CONFIGURATION)
    dispatch(botsApi.util.invalidateTags([ServerApiTag.BOTS_METADATA_LIST]))
  })

  socket.on(SocketServerEvent.KB_INDEXING_DONE, (modelId, result: IndexRunResult) => {
    dispatch(
      knowledgeBaseApi.util.invalidateTags([
        ServerApiTag.KNOWLEDGE_BASE_DOCUMENTS(modelId),
        ServerApiTag.KNOWLEDGE_BASE_PAST_INDEX_RUNS(modelId)
      ])
    )
    toastNotifyIndexingDone(modelId, result)
  })
}

export function emitBotState(botState: BotState | undefined) {
  SocketLog.log('Socket Out', SocketClientEvent.BOT_STATE, { botState })
  socket?.emit(SocketClientEvent.BOT_STATE, botState)
}

export function emitCommentChange() {
  SocketLog.log('Socket Out', SocketClientEvent.COMMENTS_CHANGE)
  socket?.emit(SocketClientEvent.COMMENTS_CHANGE)
}

export function emitJoinEditing() {
  SocketLog.log('Socket Out', SocketClientEvent.JOIN_EDITING)
  socket?.emit(SocketClientEvent.JOIN_EDITING)
}

export function closeSocketServer() {
  if (socket) {
    socket.close()
  }
}

export function reportOpeningExclusiveUiToServer() {
  SocketLog.log('Socket Out', SocketClientEvent.OPEN_EXCLUSIVE_UI)
  socket?.emit(SocketClientEvent.OPEN_EXCLUSIVE_UI)
}

export function reportClosingExclusiveUiToServer() {
  SocketLog.log('Socket Out', SocketClientEvent.CLOSE_EXCLUSIVE_UI)
  socket?.emit(SocketClientEvent.CLOSE_EXCLUSIVE_UI)
}

export function getSocketId(): string | undefined {
  if (!socket) {
    return
  }

  return socket.id
}

export function generateApiUrl(user: ConnectedSocketUser, botId: BotId): string {
  const userString = encodeURIComponent(JSON.stringify(user))

  // @ts-ignore
  return `${getApiUrl().replace('/api', '')}?botId=${botId}&user=${userString}`
}
